<script setup>
import { computed, onMounted, ref, toValue, watch } from 'vue';
import { useStore } from 'vuex';
import api from '../../api.js';
import { adventCalendarActive, asset, blackweekItemActive, filterProductCategoryBy, getMarketDashboardLink } from '../../helpers.js';

const store = useStore();

const products = ref([]);
const productGroups = ref([]);
const analyses = ref([]);
const hoveredSubmenu = ref(null);
const DASHBOARD_BASE = '/dashboard';

const navigationItems = computed(() => {
    const items = [
        { name: 'dashboard_heading_pricetargets', icon: 'fi fi-rr-bullseye-arrow', link: `${DASHBOARD_BASE}/price-targets` },
        {
            name: 'nav_portfolio',
            icon: 'fi fi-rr-stats',
            link: null,
            children: [
                { name: 'nav_long_term_trades', icon: 'fi fi-rr-chart-pie-alt', link: `${DASHBOARD_BASE}/trades` },
                { name: 'nav_short_term_trades', icon: 'fi fi-rs-chart-candlestick', link: `${DASHBOARD_BASE}/trading-depot` }
            ]
        },
        { name: 'nav_news', icon: 'fi fi-rr-graduation-cap', link: `${DASHBOARD_BASE}/hkcmnews` },
        { name: 'nav_our_charts', icon: 'fi fi-tr-chart-candlestick', link: `${DASHBOARD_BASE}/charts` }
    ];

    if (adventCalendarActive()) {
        items.push({ name: 'nav_adventcalendar', icon: 'fi fi-rr-hat-santa', link: `${DASHBOARD_BASE}/adventskalender` });
    }

    if (blackweekItemActive('masterclass')) {
        items.push({ name: 'masterclass', icon: 'fi fi-rr-star', link: `${DASHBOARD_BASE}/masterclass` });
    }

    return items;
});

const isSidebarOpen = computed(() => store.getters['settings/getBackendSidebarStateByName']('backend_sidebar'));

const isContentLoaded = (content) => computed(() => store.getters['settings/isContentLoaded'](content));

const ENDPOINTS = {
    FILTERS: '/api/v1/contentstreams/filters',
    PRODUCT_CATEGORIES: '/api/v1/product-categories/all'
};

const PLACEHOLDER_IMG = asset('/hkcm/images/logo-small-on-light-background.svg', 'public');

const waitForContent = (content, { timeout = 10000 } = {}) =>
    new Promise((resolve) => {
        const ready = isContentLoaded(content);

        if (toValue(ready)) return resolve();

        const stop = watch(
            ready,
            (val) => {
                if (val) {
                    clearTimeout(timer);
                    stop();
                    resolve();
                }
            },
            { immediate: true }
        );

        const timer = setTimeout(() => {
            stop();
            resolve();
        }, timeout);
    });

/**
 * Use store cache if available, otherwise fall back to a single categories payload.
 * Mutates each product with link/image/readonly flags.
 */
const hydrateProductsWithGroups = async (rawProducts, categoriesPayload) => {
    const categories = categoriesPayload?.data ?? categoriesPayload ?? [];
    const getGroupByProductId = store.getters['product_categories/getGroupByProductId'];

    const applyGroupToProduct = async (product, group) => {
        if (!group) {
            return {
                ...product,
                link: '',
                image: PLACEHOLDER_IMG,
                readonly: true
            };
        }

        const { productType, products, id, image } = group;

        let link;
        let lastContentstreamId = null;

        const isBundle = productType === 'singleProduct' || productType === 'doubleProduct';

        if (isBundle) {
            const items = products?.data ?? [];

            const productIds = items
                .filter((p) => p?.id && !p?.name?.de?.includes('Video'))
                .map((p) => p.id)
                .filter(Boolean);

            if (productIds.length) {
                const latest = await getLatestContentstreamByProductId(productIds);
                lastContentstreamId = latest?.id;

                if (lastContentstreamId) {
                    link = `/dashboard/markets-analysis/${lastContentstreamId}`;
                }
            }
        }

        if (!isBundle || !link) {
            // Fallback to generic dashboard link
            link = getMarketDashboardLink(productType, id);
        }

        return {
            ...product,
            link,
            image: image ? image.replace('.png', '-gradient.png') : product.image,
            readonly: false
        };
    };

    const hydrateSingle = async (product) => {
        const cachedGroup = typeof getGroupByProductId === 'function' ? getGroupByProductId(product.id) : null;

        const group = cachedGroup || filterProductCategoryBy(product.id, categories);

        // Always go through the same path, always return a Promise
        return applyGroupToProduct(product, group);
    };

    // Ensure we resolve all async work
    return Promise.all(rawProducts.map(hydrateSingle));
};

const makeLatestKey = (productIds = []) => `latestContentstream:${[...productIds].filter(Boolean).sort().join(',')}`;

const getLatestContentstreamByProductId = async (productIds) => {
    try {
        const key = makeLatestKey(productIds);

        let response = await store.getters['contentstreams/getByKey'](key);

        if (!response?.data) {
            response = await store.dispatch('contentstreams/index', {
                params: {
                    'sort[by]': 'published_at',
                    'sort[order]': 'desc',
                    page_size: 1,
                    page: 1,
                    product_id: productIds,
                    key
                }
            });
        }

        return response?.data?.[0] ?? null;
    } catch (err) {
        console.error('Failed to fetch latest contentstream:', err);

        return null;
    }
};

/**
 * Initialize the products list from /contentstreams/filters
 * then enrich using either cached store data or a single categories response.
 */
const initProductGroups = async () => {
    try {
        const { data } = await api.get(ENDPOINTS.FILTERS);
        const pww = data?.products_without_webinars ?? {};

        products.value = Object.entries(pww).map(([id, name]) => ({
            id,
            name,
            link: '',
            readonly: true,
            image: PLACEHOLDER_IMG
        }));

        let categoriesPayload = null;
        try {
            const res = await api.get(ENDPOINTS.PRODUCT_CATEGORIES);
            categoriesPayload = res?.data?.data ?? res?.data;
        } catch (e) {}

        products.value = await hydrateProductsWithGroups(products.value, categoriesPayload);
    } catch (err) {
        products.value = [];
    }
};

/**
 * Build unique product groups used in the portfolio view from loaded analyses.
 */
const initProductGroupsForPortfolio = () => {
    const dashboard = store.getters['contentstreams/all']?.dashboard?.data ?? [];
    analyses.value = Array.isArray(dashboard) ? dashboard : [];

    const seen = new Set();
    productGroups.value = [];

    for (const analysis of analyses.value) {
        const product = analysis?.product;

        if (!product) continue;

        const name = product.productGroups?.data?.[0]?.name?.de;

        if (!name || seen.has(name)) continue;

        seen.add(name);
        productGroups.value.push(product);
    }
};

onMounted(async () => {
    await waitForContent('contentstreams');
    await initProductGroups();
    initProductGroupsForPortfolio();
});
</script>

<template>
    <div class="px-4">
        <router-link
            to="/dashboard"
            title="Dashboard"
            exact-active-class="bg-blue"
            :class="isSidebarOpen ? 'pl-8' : 'justify-center py-1'"
            class="text-white flex gap-3 items-center w-full px-4 py-2 rounded-xl hover:scale-105 duration-300"
        >
            <i class="fi fi-rr-apps flex align-middle text-white" aria-hidden="true"></i>
            <span v-if="isSidebarOpen" class="whitespace-nowrap truncate">
                {{ $t('plain_dashboard') }}
            </span>
        </router-link>
    </div>

    <div class="group">
        <div class="px-4">
            <router-link
                to="/dashboard/markets"
                :title="$t('plain_analysis')"
                exact-active-class="bg-blue"
                :class="isSidebarOpen ? 'pl-8' : 'justify-center py-3'"
                class="text-white flex gap-3 items-center w-full px-4 py-2 rounded-xl relative hover:scale-105 duration-300"
            >
                <i class="fi fi-rr-document flex align-middle text-white" aria-hidden="true"></i>
                <span v-if="isSidebarOpen" class="whitespace-nowrap truncate">
                    {{ $t('nav_analysis') }}
                </span>
            </router-link>
        </div>

        <span
            v-if="productGroups.length > 0"
            class="group-hover:opacity-100 group-hover:shadow-md delay-200 group-hover:right-[-400px] right-[1100px] px-4 rounded-xl absolute translate-y-[-5rem] bg-blue max-h-[500px] w-[400px] overflow-y-scroll z-50"
        >
            <p class="font-bold pb-4 sticky top-0 bg-gradient-to-b from-blue from-60% bg-transparent to-100% p-4 z-50">
                {{ $t('your_analyses') }}
            </p>

            <div class="grid grid-cols-4 gap-4 pb-10">
                <template v-for="productGroup in products" :key="productGroup.id">
                    <router-link :to="productGroup.link || '/dashboard/markets'" class="hover:scale-110 duration-300" :aria-disabled="productGroup.readonly">
                        <div class="flex flex-row justify-between items-center mb-4">
                            <div class="flex gap-4 items-center rounded-xl hover:shadow-md">
                                <img class="h-auto w-16 duration-200 m-auto object-contain" :src="productGroup.image" :alt="productGroup.name" loading="lazy" />
                            </div>
                        </div>
                    </router-link>
                </template>
            </div>

            <p class="font-bold pb-1 sticky bottom-0 bg-gradient-to-t from-blue from-40% bg-transparent to-100% p-2 z-50"></p>
        </span>
    </div>

    <div class="px-4">
        <router-link
            to="/dashboard/markets#special-reports"
            :title="$t('contenstream_category_special_reports')"
            :class="isSidebarOpen ? 'pl-8' : 'justify-center py-1'"
            class="text-white flex gap-3 items-center w-full px-4 py-2 rounded-xl hover:scale-105 duration-300 specialReports-link mt-3"
        >
            <i class="fi fi-tr-newspaper flex align-middle text-white specialReports-icon" aria-hidden="true"></i>
            <span v-if="isSidebarOpen" class="whitespace-nowrap truncate"> {{ $t('contenstream_category_special_reports') }} </span>
        </router-link>
    </div>

    <div v-for="item in navigationItems" :key="item.name" class="px-4">
        <router-link
            v-if="!item.children"
            :to="item.link"
            :title="$t(item.name)"
            exact-active-class="bg-blue"
            :class="[
                isSidebarOpen ? 'pl-8 my-2' : 'justify-center py-3 my-2',
                item.name === 'nav_adventcalendar' ? 'advent-link' : '',
                item.name === 'masterclass' ? 'masterclass-link' : ''
            ]"
            class="text-white flex gap-3 items-center w-full px-4 py-2 rounded-xl hover:scale-105 duration-300"
        >
            <i
                :class="[item.icon, item.name === 'nav_adventcalendar' ? 'advent-icon' : '', item.name === 'masterclass' ? 'masterclass-icon' : '']"
                class="flex align-middle text-white"
                aria-hidden="true"
            ></i>
            <span v-if="isSidebarOpen" class="whitespace-nowrap truncate"> {{ $t(item.name) }}</span>
        </router-link>

        <div
            v-else
            class="relative flyout-container"
            :class="{ 'sidebar-open': isSidebarOpen }"
            @mouseenter="if (!isSidebarOpen) hoveredSubmenu = item.name;"
            @mouseleave="if (!isSidebarOpen) hoveredSubmenu = null;"
        >
            <div :class="isSidebarOpen ? 'pl-8 my-2' : 'justify-center py-3 my-2'" class="text-white flex gap-3 items-center w-full px-4 py-2 rounded-xl text-left">
                <i :class="item.icon" class="flex align-middle text-white" aria-hidden="true"></i>
                <span v-if="isSidebarOpen" class="whitespace-nowrap truncate flex-1">{{ $t(item.name) }}</span>
            </div>
            <div v-if="isSidebarOpen" class="pl-12">
                <router-link
                    v-for="child in item.children"
                    :key="child.name"
                    :to="child.link"
                    :title="$t(child.name)"
                    exact-active-class="bg-blue"
                    class="text-white flex gap-3 items-center w-full pl-4 py-2 rounded-xl hover:scale-105 duration-300 my-1"
                >
                    <i :class="child.icon" class="flex align-middle text-white text-sm" aria-hidden="true"></i>
                    <span class="whitespace-nowrap truncate">{{ $t(child.name) }}</span>
                </router-link>
            </div>

            <div v-if="!isSidebarOpen && hoveredSubmenu === item.name" class="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-60 bg-blue-dark rounded-xl shadow-lg p-2 z-50">
                <p class="px-4 py-2 text-sm font-bold text-white border-b border-blue/50 mb-2">{{ $t(item.name) }}</p>
                <router-link
                    v-for="child in item.children"
                    :key="child.name"
                    :to="child.link"
                    :title="$t(child.name)"
                    exact-active-class="bg-blue"
                    class="text-white flex gap-3 items-center w-full px-4 py-2 rounded-xl hover:bg-blue/50 duration-300 my-1"
                >
                    <i :class="child.icon" class="flex align-middle text-white text-sm" aria-hidden="true"></i>
                    <span class="whitespace-nowrap truncate">{{ $t(child.name) }}</span>
                </router-link>
            </div>
        </div>
    </div>
</template>

<style scoped>
.flyout-container:not(.sidebar-open)::after {
    content: '';
    position: absolute;
    top: 0;
    left: 100%;
    width: 0.5rem;
    height: 100%;
}

.badge-gold {
    background: linear-gradient(145deg, #d4af37, #b9962e);
    color: #fff;
}

.specialReports-link {
    box-shadow: 0 0 0 1px rgba(212, 175, 55, 0.6);
    background-image: radial-gradient(circle at left, rgba(212, 175, 55, 0.16), transparent 60%);
}

.specialReports-link:hover {
    box-shadow:
        0 0 0 1px rgba(212, 175, 55, 0.85),
        0 8px 18px rgba(0, 0, 0, 0.35);
}

.specialReports-icon {
    color: #f5e4b0;
    filter: drop-shadow(0 0 4px rgba(212, 175, 55, 0.75));
}

.advent-link {
    box-shadow: 0 0 0 1px rgba(212, 175, 55, 0.6);
    background-image: radial-gradient(circle at left, rgba(212, 175, 55, 0.16), transparent 60%);
}

.advent-link:hover {
    box-shadow:
        0 0 0 1px rgba(212, 175, 55, 0.85),
        0 8px 18px rgba(0, 0, 0, 0.35);
}

.advent-icon {
    color: #f5e4b0;
    filter: drop-shadow(0 0 4px rgba(212, 175, 55, 0.75));
}

.masterclass-link {
    box-shadow: 0 0 0 1px rgb(0, 48, 197);
    background-image: radial-gradient(circle at left, rgba(0, 150, 255, 0.12), transparent 60%);
}

.masterclass-link:hover {
    box-shadow:
        0 0 0 1px rgb(84, 122, 255),
        0 8px 18px rgba(0, 0, 0, 0.35);
}

.masterclass-icon {
    color: #a7baf8;
    filter: drop-shadow(0 0 4px rgb(114, 146, 251));
}
</style>
